import { useState } from 'react';
import { motion } from 'framer-motion';
import { Cpu, ShieldCheck, Link2, Database, Info, Loader2, CheckCircle2, ExternalLink } from 'lucide-react';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Button } from '@/components/ui/button';
import { Card } from '@/components/ui/card';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { useQRStore } from '@/stores/qrStore';
import { supabase } from '@/integrations/supabase/client';
import { useToast } from '@/hooks/use-toast';
import { Switch } from '@/components/ui/switch';
import { Textarea } from '@/components/ui/textarea';

interface BlockchainFormProps {
  contentData: Record<string, string>;
  onUpdate: (data: Record<string, string>) => void;
}

export function BlockchainForm({ contentData, onUpdate }: BlockchainFormProps) {
  const { currentQR } = useQRStore();
  const { toast } = useToast();
  const [isMinting, setIsMinting] = useState(false);
  const [mintResult, setMintResult] = useState<{ txHash: string; tokenId: string } | null>(null);

  const handleMint = async () => {
    if (!currentQR.id) {
      toast({
        title: "Save QR Code First",
        description: "Please save your QR code before minting its authenticity NFT.",
        variant: "destructive",
      });
      return;
    }

    setIsMinting(true);
    try {
      const { data, error } = await supabase.functions.invoke('mint-nft-on-scan', {
        body: {
          qr_code_id: currentQR.id,
          user_identifier: 'OWNER_CERTIFICATE',
          metadata: {
            name: contentData.assetName || 'Authenticity Certificate',
            description: contentData.description || 'Verified Authenticity Certificate for this NexQR code.',
            image: contentData.imageUrl || 'https://nexqr.online/nft-placeholder.png',
            network: contentData.network || 'polygon'
          }
        }
      });

      if (error) throw error;

      if (data?.success) {
        setMintResult({
          txHash: data.transaction_hash,
          tokenId: data.token_id
        });
        
        onUpdate({
          ...contentData,
          txHash: data.transaction_hash,
          tokenId: data.token_id,
          mintStatus: 'MINTED'
        });

        toast({
          title: "NFT Minted!",
          description: "Your authenticity certificate has been recorded on the blockchain.",
        });
      }
    } catch (error: any) {
      console.error('Minting error:', error);
      toast({
        title: "Minting Failed",
        description: error.message || "An error occurred during blockchain minting.",
        variant: "destructive",
      });
    } finally {
      setIsMinting(false);
    }
  };

  return (
    <motion.div
      initial={{ opacity: 0, y: 10 }}
      animate={{ opacity: 1, y: 0 }}
      className="space-y-6"
    >
      <div className="flex items-center gap-2">
        <Cpu className="h-5 w-5 text-primary" />
        <h3 className="text-sm font-medium text-muted-foreground uppercase tracking-wider">
          Blockchain Authenticity
        </h3>
      </div>

      <Card className="p-4 space-y-4">
        <Alert className="bg-blue-500/5 border-blue-500/20 text-blue-600">
          <ShieldCheck className="h-4 w-4" />
          <AlertDescription>
            Mint a digital certificate or NFT for this QR code to ensure its authenticity and prevent counterfeiting.
          </AlertDescription>
        </Alert>

        <div className="space-y-4">
          <div className="flex items-center justify-between p-3 rounded-xl bg-muted/30 border border-muted-foreground/10">
            <div className="space-y-0.5">
              <Label className="text-sm font-semibold">Enable Proof-of-Scan</Label>
              <p className="text-[10px] text-muted-foreground">Allows users who scan this QR to mint their own NFT proof.</p>
            </div>
            <Switch 
              checked={contentData.mintOnScan === 'true'} 
              onCheckedChange={checked => onUpdate({ ...contentData, mintOnScan: String(checked) })}
            />
          </div>

          <div className="space-y-2">
            <Label>Blockchain Network</Label>
            <Select 
              value={contentData.network || 'polygon'} 
              onValueChange={v => onUpdate({ ...contentData, network: v })}
            >
              <SelectTrigger>
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="polygon">Polygon (Eco-friendly)</SelectItem>
                <SelectItem value="ethereum">Ethereum</SelectItem>
                <SelectItem value="solana">Solana</SelectItem>
                <SelectItem value="base">Base (Coinbase)</SelectItem>
              </SelectContent>
            </Select>
          </div>

          <div className="space-y-2">
            <Label>Asset Name / Serial Number</Label>
            <Input 
              placeholder="NX-2026-0001" 
              value={contentData.assetName || ''} 
              onChange={e => onUpdate({ ...contentData, assetName: e.target.value })}
            />
          </div>

          <div className="space-y-2">
            <Label>Description</Label>
            <Textarea 
              placeholder="This is a certified authentic product..." 
              value={contentData.description || ''} 
              onChange={e => onUpdate({ ...contentData, description: e.target.value })}
              className="min-h-[80px]"
            />
          </div>

          <div className="space-y-2">
            <Label>Verification URL</Label>
            <Input 
              placeholder="https://explorer.polygon.technology/..." 
              value={contentData.url || ''} 
              onChange={e => onUpdate({ ...contentData, url: e.target.value })}
            />
          </div>

          <div className="pt-4 border-t">
            <h4 className="text-xs font-semibold uppercase tracking-wider text-muted-foreground mb-3">On-Chain Status</h4>
            <div className="p-4 rounded-xl bg-slate-900 text-white font-mono text-[10px] space-y-1 relative overflow-hidden">
              {mintResult || contentData.txHash ? (
                <div className="absolute top-2 right-2">
                  <CheckCircle2 className="h-4 w-4 text-green-400" />
                </div>
              ) : null}
              
              <div className="flex justify-between">
                <span className="text-white/40">Status:</span>
                <span className={mintResult || contentData.txHash ? "text-green-400" : "text-yellow-400"}>
                  {mintResult || contentData.txHash ? 'VERIFIED_ON_CHAIN' : 'PENDING_MINT'}
                </span>
              </div>
              <div className="flex justify-between">
                <span className="text-white/40">Token ID:</span>
                <span>{mintResult?.tokenId || contentData.tokenId || 'N/A'}</span>
              </div>
              <div className="flex justify-between">
                <span className="text-white/40">Network:</span>
                <span className="uppercase">{contentData.network || 'polygon'}</span>
              </div>
              {(mintResult?.txHash || contentData.txHash) && (
                <div className="mt-2 pt-2 border-t border-white/10">
                  <span className="text-white/40 block mb-1">TX Hash:</span>
                  <div className="flex items-center justify-between gap-2">
                    <span className="truncate flex-1">{mintResult?.txHash || contentData.txHash}</span>
                    <a 
                      href={`https://polygonscan.com/tx/${mintResult?.txHash || contentData.txHash}`} 
                      target="_blank" 
                      rel="noopener noreferrer"
                      className="text-blue-400 hover:text-blue-300"
                    >
                      <ExternalLink className="h-3 w-3" />
                    </a>
                  </div>
                </div>
              )}
            </div>
          </div>

          <Button 
            className="w-full bg-slate-900 hover:bg-slate-800 text-white gap-2"
            onClick={handleMint}
            disabled={isMinting || !!(mintResult || contentData.txHash)}
          >
            {isMinting ? (
              <>
                <Loader2 className="h-4 w-4 animate-spin" />
                Minting NFT Certificate...
              </>
            ) : (
              <>
                <Link2 className="h-4 w-4" />
                {mintResult || contentData.txHash ? 'Authenticity NFT Minted' : 'Mint Authenticity NFT'}
              </>
            )}
          </Button>
          {!currentQR.id && !mintResult && !contentData.txHash && (
            <p className="text-[10px] text-center text-muted-foreground italic">
              * Save QR code first to enable blockchain minting
            </p>
          )}
        </div>
      </Card>
    </motion.div>
  );
}
